import { z } from 'zod';
import { command, del, get, post } from '../http';
import { type ListQuery, paginated, resource } from '../schemas/common';
import { withdrawalSchema, WITHDRAWAL_MIN_LDS } from '../schemas/domain';

/**
 * 2026-08-02 automatic on-chain LDS withdrawal (admin-only surface). The
 * private key itself never appears in any of these shapes — only the
 * derived, non-sensitive wallet_address (see HotWalletKeyStore's docblock on
 * the API side for why: the response is deliberately write-only).
 */
const autoWithdrawalSettingsSchema = z.object({
  enabled: z.boolean(),
  has_key: z.boolean(),
  wallet_address: z.string().nullable(),
  key_set_at: z.string().nullable(),
  max_per_tx_lds: z.string(),
  max_per_day_lds: z.string(),
  gas_limit_units: z.number(),
  hot_wallet_bnb_balance: z.string().nullable(),
  hot_wallet_lds_balance: z.string().nullable(),
});
export type AutoWithdrawalSettings = z.infer<typeof autoWithdrawalSettingsSchema>;

const autoWithdrawalReportSchema = z.object({
  auto_paid_count: z.number(),
  auto_paid_total_lds: z.string(),
  auto_paid_today_lds: z.string(),
  manual_pending_count: z.number(),
  failed_count: z.number(),
  failed: z.array(
    z.object({
      id: z.string(),
      user_code: z.string().nullable(),
      amount_lds: z.string(),
      reject_reason: z.string().nullable(),
      processed_at: z.string().nullable(),
    }),
  ),
});
export type AutoWithdrawalReport = z.infer<typeof autoWithdrawalReportSchema>;

/**
 * Alpha One terms (§8, amended 2026-08-01): no admin charge, 750 LDS flat
 * minimum, 24×7 auto withdrawal. The client's explicit 2026-08-01 requirement
 * is that this flow is LDS-only end to end — USD/USDT is never sent, quoted,
 * or displayed on the withdrawal page. Do NOT reintroduce the legacy 7% fee /
 * $5 (or USD-denominated) minimum / withdrawal-window rules.
 */
// wallet_address is NOT collected here — 2026-07-31. Withdrawals always pay
// out to the member's own registered wallet (verified at registration/login),
// never a request-supplied address. See WithdrawalController::store().
// 2026-09-11: label configurable — "LDS" on the main site, "USDT" on the
// lds1 build (VITE_WITHDRAWAL_LABEL, mirrors apps/user/src/lib/format.ts).
const WITHDRAWAL_UNIT_LABEL =
  (typeof import.meta !== 'undefined' && (import.meta as { env?: Record<string, unknown> }).env?.VITE_WITHDRAWAL_LABEL) ||
  'LDS';

export const withdrawalInput = z.object({
  amount_lds: z.coerce
    .number()
    .min(WITHDRAWAL_MIN_LDS, `Minimum withdrawal is ${WITHDRAWAL_MIN_LDS} ${WITHDRAWAL_UNIT_LABEL}`),
  transaction_pin: z.string().min(4, 'Transaction PIN required'),
});
export type WithdrawalInput = z.infer<typeof withdrawalInput>;

/**
 * LDS-only quote shape (2026-08-01). Deliberately has NO available_usd /
 * min_usd / fee_usd / net_usd fields — the client does not want a USD figure
 * reachable anywhere on this page, even in a field the UI happens not to
 * render. lds_rate_usd is kept only as the conversion rate used server-side;
 * the withdrawal page must not render it as a dollar amount.
 */
const withdrawalQuote = z.object({
  available_lds: z.string(),
  min_lds: z.string(),
  fee_percent: z.number(),
  fee_lds: z.string(),
  net_lds: z.string(),
  lds_rate_usd: z.string(),
});

export const withdrawalsApi = {
  quote: (amountLds: number) => get('/withdrawals/quote', resource(withdrawalQuote), { amount_lds: amountLds }),

  create: (input: WithdrawalInput) => post('/withdrawals', resource(withdrawalSchema), input),

  list: (query: ListQuery = {}) => get('/withdrawals', paginated(withdrawalSchema), query),

  /* admin */
  adminList: (status: 'pending' | 'approved' | 'rejected' | 'all', query: ListQuery = {}) =>
    get(`/admin/withdrawals/${status}`, paginated(withdrawalSchema), query),
  approve: (id: string, note?: string) => command(`/admin/withdrawals/${id}/approve`, { note }),
  reject: (id: string, note: string) => command(`/admin/withdrawals/${id}/reject`, { note }),
  report: (query: ListQuery = {}) =>
    get(
      '/admin/withdrawals/report',
      resource(
        z.object({
          total_usd: z.string(),
          total_count: z.number(),
          rows: z.array(z.object({ date: z.string(), count: z.number(), amount_usd: z.string() })),
        }),
      ),
      query,
    ),

  /* auto-withdrawal (2026-08-02) */
  autoWithdrawal: {
    settings: () => get('/admin/auto-withdrawal/settings', resource(autoWithdrawalSettingsSchema)),
    updateSettings: (input: {
      enabled: boolean;
      max_per_tx_lds: number | string;
      max_per_day_lds: number | string;
      gas_limit_units: number;
    }) => command('/admin/auto-withdrawal/settings', input),
    /** Write-only: response is just the derived address, never the key. */
    setWalletKey: (privateKey: string) =>
      post(
        '/admin/auto-withdrawal/wallet-key',
        resource(z.object({ wallet_address: z.string() })),
        { private_key: privateKey },
      ),
    clearWalletKey: () => del('/admin/auto-withdrawal/wallet-key', z.object({ message: z.string() })),
    retryPayout: (withdrawalId: string) =>
      post(
        `/admin/withdrawals/${withdrawalId}/retry-auto-payout`,
        resource(z.object({ tx_hash: z.string().nullable() })),
      ),
    report: () => get('/admin/auto-withdrawal/report', resource(autoWithdrawalReportSchema)),
  },
};
